import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Plus, Flame, Calendar, Package, ArrowRight, Trash2, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ClientModal from "@/components/ClientModal";

const STAGES = [
    { key: "all", label: "All Clients" },
    { key: "Enquiry", label: "Enquiry" },
    { key: "InProgress", label: "In Progress" },
    { key: "PostProduction", label: "Post-Production" },
    { key: "Completed", label: "Completed" },
];

const STAGE_BADGE = {
    Enquiry: "bg-gray-100 text-gray-700 border-gray-200",
    InProgress: "bg-blue-50 text-blue-700 border-blue-200",
    PostProduction: "bg-amber-50 text-amber-800 border-amber-200",
    Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const STAGE_LABEL = {
    Enquiry: "Enquiry",
    InProgress: "In Progress",
    PostProduction: "Post-Production",
    Completed: "Completed",
};

function formatDate(d) {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return d;
    }
}

export default function DashboardPage() {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [summary, setSummary] = useState({ total: 0, fast_track: 0, stages: {} });
    const [filter, setFilter] = useState("all");
    const [fastOnly, setFastOnly] = useState(false);
    const [q, setQ] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Sort State: "asc" (Soonest Delivery) | "desc" (Latest Delivery)
    const [sortOrder, setSortOrder] = useState("desc");

    const loadClients = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== "all") params.stage = filter;
            if (fastOnly) params.fast_track = true;
            if (q) params.q = q;
            const [c, s] = await Promise.all([
                api.get("/clients", { params }),
                api.get("/clients/summary"),
            ]);
            setClients(c.data || []);
            setSummary(s.data || { total: 0, fast_track: 0, stages: {} });
        } catch (e) {
            toast.error("Failed to load clients");
        } finally {
            setLoading(false);
        }
    };

    // Delete Client Handler
    const handleDelete = async (clientId, clientName) => {
        if (!window.confirm(`Are you sure you want to delete ${clientName}?`)) {
            return;
        }

        try {
            await api.delete(`/clients/${clientId}`);
            toast.success("Client deleted successfully");
            await loadClients(); // Refresh list & summary stats
        } catch (e) {
            toast.error("Failed to delete client");
        }
    };

    useEffect(() => {
        loadClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, fastOnly, q]);

    useEffect(() => {
        const h = (e) => setQ(e.detail || "");
        window.addEventListener("global-search", h);
        return () => window.removeEventListener("global-search", h);
    }, []);

    const title = useMemo(() => {
        if (fastOnly) return "Fast Track Projects";
        return STAGES.find((s) => s.key === filter)?.label || "All Clients";
    }, [filter, fastOnly]);

    // Sorted Clients List Memo
    const sortedClients = useMemo(() => {
        return [...clients].sort((a, b) => {
            const dateA = a.expected_delivery_date ? new Date(a.expected_delivery_date).getTime() : Infinity;
            const dateB = b.expected_delivery_date ? new Date(b.expected_delivery_date).getTime() : Infinity;

            return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        });
    }, [clients, sortOrder]);

    const handleSaved = async () => {
        setModalOpen(false);
        setEditingClient(null);
        await loadClients();
    };

    return (
        <div className="p-8 lg:p-10" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-gray-400 mb-2">Studio overview</div>
                    <h1 className="font-serif text-4xl md:text-5xl text-gray-900 tracking-tight">{title}</h1>
                    <p className="text-sm text-gray-500 mt-2">
                        {summary.total} total clients · {summary.fast_track} fast-track in flight
                    </p>
                </div>
                <Button
                    onClick={() => {
                        setEditingClient(null);
                        setModalOpen(true);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="create-client-button"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Create Client
                </Button>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <StatTile label="Enquiries" value={summary.stages?.Enquiry || 0} accent="gray" testId="stat-enquiry" />
                <StatTile label="In Progress" value={summary.stages?.InProgress || 0} accent="blue" testId="stat-inprogress" />
                <StatTile label="Post-Production" value={summary.stages?.PostProduction || 0} accent="amber" testId="stat-postprod" />
                <StatTile label="Completed" value={summary.stages?.Completed || 0} accent="emerald" testId="stat-completed" />
            </div>

            {/* Filters & Sort Controls Row */}
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                {/* Stage Filters */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="stage-filter-tabs">
                    {STAGES.map((s) => {
                        const count = s.key === "all" ? summary.total : summary.stages?.[s.key] || 0;
                        const active = filter === s.key && !fastOnly;
                        return (
                            <button
                                key={s.key}
                                onClick={() => {
                                    setFilter(s.key);
                                    setFastOnly(false);
                                }}
                                data-testid={`filter-${s.key}`}
                                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                                    active
                                        ? "bg-blue-500 border-blue-500 text-white"
                                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                            >
                                {s.label}
                                <span className={`text-xs tabular-nums ${active ? "text-white/80" : "text-gray-400"}`}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => {
                            setFastOnly((v) => !v);
                            setFilter("all");
                        }}
                        data-testid="filter-fast-track"
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
                            fastOnly
                                ? "bg-amber-500 border-amber-500 text-white"
                                : "bg-white border-amber-200 text-amber-700 hover:border-amber-300"
                        }`}
                    >
                        <Flame className="w-3.5 h-3.5" /> Fast Track
                        <span className={`text-xs tabular-nums ${fastOnly ? "text-white/90" : "text-amber-500"}`}>
                            {summary.fast_track}
                        </span>
                    </button>
                </div>

                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-medium flex items-center gap-1">
                        <ArrowUpDown className="w-3.5 h-3.5" /> Delivery:
                    </span>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-sm rounded-md px-3 py-1.5 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
                        data-testid="sort-delivery-date"
                    >
                        <option value="asc">Earliest First (Soonest)</option>
                        <option value="desc">Latest First</option>
                    </select>
                </div>
            </div>

            {/* Cards grid */}
            {loading ? (
                <div className="text-sm text-gray-500">Loading…</div>
            ) : sortedClients.length === 0 ? (
                <EmptyState onCreate={() => setModalOpen(true)} />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="clients-grid">
                    {sortedClients.map((c, i) => (
                        <ClientCard
                            key={c.id}
                            c={c}
                            index={i}
                            onOpen={() => navigate(`/clients/${c.id}`)}
                            onEdit={() => {
                                setEditingClient(c);
                                setModalOpen(true);
                            }}
                            onDelete={() => handleDelete(c.id, c.name)}
                        />
                    ))}
                </div>
            )}

            <ClientModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setEditingClient(null);
                }}
                onSaved={handleSaved}
                initial={editingClient}
            />
        </div>
    );
}

function StatTile({ label, value, accent, testId }) {
    const color = {
        gray: "text-gray-900",
        blue: "text-blue-600",
        amber: "text-amber-600",
        emerald: "text-emerald-600",
    }[accent];
    return (
        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest text-gray-400">{label}</div>
            <div className={`mt-2 font-serif text-3xl tabular-nums ${color}`}>{value}</div>
        </div>
    );
}

function ClientCard({ c, index, onOpen, onEdit, onDelete }) {
    const events = c.events || [];

    return (
        <div
            className="group bg-white border border-gray-200 rounded-md p-5 hover:border-blue-200 hover:shadow-sm transition-all card-fade-in flex flex-col justify-between"
            style={{ animationDelay: `${index * 40}ms` }}
            data-testid={`client-card-${c.id}`}
        >
            <div>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="min-w-0">
                        <h3 className="font-serif text-xl text-gray-900 leading-tight truncate">{c.name}</h3>
                        <div className="text-xs text-gray-500 mt-1">{c.package?.album_type || "—"} album</div>
                    </div>
                    {c.is_fast_track && (
                        <span
                            className="fast-track-pulse inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`fast-track-badge-${c.id}`}
                        >
                            <Flame className="w-3 h-3" /> Fast
                        </span>
                    )}
                </div>

                {/* Details Section */}
                <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                        <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                STAGE_BADGE[c.current_stage] || "bg-gray-100 text-gray-700 border-gray-200"
                            }`}
                        >
                            {STAGE_LABEL[c.current_stage] || c.current_stage}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span className="tabular-nums font-medium text-gray-800">
                            Delivery {formatDate(c.expected_delivery_date)}
                        </span>
                    </div>

                    <div className="flex items-center gap-2 text-gray-600">
                        <Package className="w-3.5 h-3.5 text-gray-400" />
                        <span className="tabular-nums">
                            ₹{((c.package?.total_committed_amount) || 0).toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Event Dates Section */}
                {events.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-1.5">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex items-center justify-between">
                            <span>Events ({events.length})</span>
                        </div>
                        <div className="space-y-1">
                            {events.slice(0, 2).map((ev, idx) => (
                                <div key={ev.id || idx} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50/60 px-2 py-1 rounded border border-gray-100">
                                    <span className="font-medium text-gray-700 truncate pr-2">
                                        {ev.event_name || ev.type || ev.title || `Event ${idx + 1}`}
                                    </span>
                                    <span className="tabular-nums text-gray-500 shrink-0">
                                        {formatDate(ev.date || ev.event_date || ev.start_date || ev.timestamp)}
                                    </span>
                                </div>
                            ))}
                            {events.length > 2 && (
                                <div className="text-[10px] text-gray-400 text-right font-medium pt-0.5">
                                    + {events.length - 2} more event{events.length - 2 > 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Actions Footer */}
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onEdit}
                        className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                        data-testid={`edit-client-${c.id}`}
                    >
                        Edit
                    </button>
                    <button
                        onClick={onDelete}
                        className="text-xs text-red-500 hover:text-red-700 transition-colors inline-flex items-center gap-1"
                        data-testid={`delete-client-${c.id}`}
                    >
                        <Trash2 className="w-3 h-3" /> Delete
                    </button>
                </div>
                <button
                    onClick={onOpen}
                    className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                    data-testid={`view-client-${c.id}`}
                >
                    View details <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

function EmptyState({ onCreate }) {
    return (
        <div className="bg-white border border-dashed border-gray-200 rounded-md p-12 text-center" data-testid="empty-state">
            <img
                src="https://images.pexels.com/photos/19685615/pexels-photo-19685615.jpeg"
                alt=""
                className="mx-auto rounded-md object-cover editorial-hero"
                style={{ width: 220, height: 160 }}
            />
            <h3 className="font-serif text-2xl mt-6 text-gray-900">No projects here yet</h3>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                Every great gallery starts with a first frame. Add a client to begin managing enquiries, shoots, teams
                and payments.
            </p>
            <Button
                onClick={onCreate}
                className="bg-blue-500 hover:bg-blue-600 mt-6"
                data-testid="empty-create-client-button"
            >
                <Plus className="w-4 h-4 mr-1.5" /> Add your first client
            </Button>
        </div>
    );
}